<template>
  <DatePicker
    ref="dateRef"
    v-model="dateInput"
    typeable
    class="tw-hidden"
    :disabled="disabled"
    @opened="adjustPosition"
    @closed="
      () => {
        // refPicker ? refPickers.blur() : dateRef.blur();
        isOpen = false;
      }
    "
  />
  <div :class="`tw-relative ${customInputClass}`">
    <BFormInput
      :model-value="formatDate(focus)"
      type="text"
      :data-cy="dataCyId"
      :placeholder="placeholder"
      :state="validState"
      :class="[
        'input-wizard-question autoFocus',
        className,
        `${validation?.$error ? 'is-invalid-input' : ''}`,
      ]"
      :disabled="disabled"
      :autofocus="isAutoFocus"
      @blur="close"
      @focus="handleFocus"
      @input="() => {}"
      @keydown="
        (e) => {
          if (e.key !== 'Tab') e.preventDefault();
        }
      "
      @click="clicked"
    />
    <div
      :class="`date-picker-icon ${classNameIcon} tw-absolute !tw-pointer-events-none tw-top-[16px] tw-right-[20px] ${
        isOpen ? 'tw-rotate-180' : ''
      }`"
    >
      <svg
        width="10"
        height="7"
        viewBox="0 0 10 7"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M0.104884 1.50596L4.67976 6.6922C4.72096 6.73575 4.77061 6.77044 4.82567 6.79415C4.88073 6.81785 4.94005 6.83008 5 6.83008C5.05995 6.83008 5.11927 6.81785 5.17433 6.79415C5.22939 6.77044 5.27904 6.73575 5.32024 6.6922L9.89512 1.50596C9.93169 1.46476 9.95972 1.41671 9.97757 1.36459C9.99541 1.31247 10.0027 1.25733 9.99909 1.20236C9.99458 1.14694 9.97901 1.093 9.95329 1.04371C9.92758 0.994417 9.89224 0.950784 9.84937 0.915386L9.07996 0.270744C9.03793 0.234389 8.98904 0.206825 8.93618 0.189682C8.88332 0.172537 8.82756 0.166162 8.77219 0.170929C8.71709 0.174915 8.66333 0.189841 8.61406 0.214834C8.56479 0.239827 8.52099 0.274386 8.48522 0.316493L5.0707 4.24673C5.04968 4.26755 5.02128 4.27924 4.99168 4.27924C4.96209 4.27924 4.93369 4.26755 4.91266 4.24673L1.51478 0.316493C1.44755 0.236188 1.35249 0.184205 1.2486 0.170929C1.19352 0.165595 1.13793 0.171309 1.08508 0.187734C1.03223 0.20416 0.983193 0.230967 0.940838 0.266585L0.150633 0.915386C0.107763 0.950784 0.0724248 0.994417 0.0467083 1.04371C0.0209918 1.093 0.0054186 1.14694 0.000909419 1.20235C-0.00273159 1.25733 0.00458484 1.31247 0.0224338 1.36459C0.0402828 1.41671 0.0683087 1.46476 0.104884 1.50596Z"
          fill="#387DFF"
        />
      </svg>
    </div>
    <b-form-invalid-feedback
      v-if="showValidContent"
      :class="{ 'is-invalid': validation.$error }"
    >
      <VRuntimeTemplate :template="validation?.$errors?.[0]?.$message" />
    </b-form-invalid-feedback>
  </div>
</template>

<script setup lang="ts">
import { convertUTCTimeToLocalTime } from "@/utils/dateTimeUtils";
import eventEmitter, { EmitterEvents } from "@/utils/eventEmitter";
import { PropType, onMounted, onUnmounted } from "vue";
import { ref, watch, nextTick } from "vue";
import DatePicker from "vue3-datepicker";
import VRuntimeTemplate from "vue3-runtime-template";

const props = defineProps({
  disabled: { type: Boolean, required: false },
  modelValue: {
    type: [Date, String] as PropType<Date | string>,
    required: false,
    default: () => null,
  },
  placeholder: { type: String, required: false, default: "Select date" },
  validState: {
    type: Boolean as PropType<boolean | null>,
    required: false,
    default: null,
  },
  dataCyId: { type: String, required: false, default: "" },
  className: { type: String, required: false, default: "" },
  classNameIcon: { type: String, required: false, default: "" },
  focus: { type: Boolean, required: false, default: null },
  isAutoFocus: { type: Boolean, required: false, default: false },
  textColor: { type: String, required: false, default: "#231f20" },
  refPicker: { type: String, required: false, default: "" },
  /** Vue Validation object */
  validation: { type: Object, default: () => ({}) },
  showValidContent: { type: Boolean, required: false, default: false },
  customInputClass: { type: String, required: false, default: "" },
});

let dateInput: any = ref(
  props.modelValue ? new Date(`${props.modelValue}`) : null,
);
const dateRef = ref();
const refPickers = ref();
const isOpen = ref(false);

onMounted(() => {
  if (dateRef.value && props.isAutoFocus) {
    dateRef.value.focus();
    isOpen.value = true;
  }
  eventEmitter.on(EmitterEvents.SCROLL_MODAL, adjustPosition);
});

onUnmounted(() => {
  eventEmitter.off(EmitterEvents.SCROLL_MODAL);
});

watch(
  () => props.modelValue,
  (newVal) => {
    if (typeof newVal === "string") {
      dateInput.value = newVal ? new Date(newVal) : null;
    }
  },
);

watch(dateInput, (newVal) => {
  emit("update:modelValue", newVal);
});

const formatDate = (val) => {
  if (dateInput.value) {
    return convertUTCTimeToLocalTime(dateInput.value);
  }
  return;
};

const clicked = () => {
  if (dateRef.value) {
    dateRef.value.focus();
    isOpen.value = true;
  }
  if (refPickers.value) {
    refPickers.value.focus();
    isOpen.value = true;
  }
};

const close = () => {
  props.validation?.$touch && props.validation.$touch();
  if (dateRef.value) {
    dateRef.value.blur();
    isOpen.value = false;
  }
  if (refPickers.value) {
    refPickers.value.blur();
    isOpen.value = false;
  }
};

const adjustPosition = () => {
  nextTick(() => {
    const datepickers = document.getElementsByClassName("v3dp__popout");
    const modals = document.getElementsByClassName("date-picker-modal");

    if (!modals.length) return;

    for (let i = 0; i < datepickers.length; i++) {
      const datepicker = datepickers[i] as HTMLElement;
      datepicker.style.position = "absolute";
      datepicker.style.transform = ``;
    }

    if (datepickers.length && modals.length) {
      const datepicker = findVisibleElement("v3dp__popout") as HTMLElement;
      if (!datepicker) return;
      const modal = modals[modals.length - 1] as HTMLElement;
      const modalRect = modal.getBoundingClientRect();
      const rect = datepicker.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // Adjust position if needed
      if (rect.bottom > modalRect.bottom) {
        datepicker.style.position = "fixed";
        if (rect.bottom > windowHeight) {
          datepicker.style.transform = `translateY(${
            windowHeight - rect.bottom - modal.scrollTop
          }px)`;
        } else {
          datepicker.style.transform = `translateY(-${modal.scrollTop}px)`;
        }
      }
    }
  });
};

const findVisibleElement = (className) => {
  const elements = document.getElementsByClassName(className);
  for (let item of elements) {
    let element = item as HTMLElement;
    if (element.style.display !== "none") {
      return element;
    }
  }
  return null; // No visible element found
};

const handleFocus = () => {
  if (props.focus) clicked();
  emit("on:focus", true);
};

defineExpose({ clicked });
const emit = defineEmits(["update:modelValue", "on:focus"]);
</script>

<style lang="scss" scoped>
input.input-wizard-question {
  border-radius: 6px;
  border: 1px solid var(--ui-rule, #eff0f1);
  background: var(--ui-rule-50, rgba(239, 240, 241, 0.5));
  padding: 18px 20px;
  height: 56px;
  color: v-bind(textColor);
  font-size: 13px;
  font-family: Poppins;
  font-style: normal;
  font-weight: 400;
  line-height: normal;

  &::placeholder {
    color: #b7b7b7;
    font-size: 15px;
    font-family: Poppins;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }
}
input.input-wizard-question[disabled] {
  color: #b7b7b7;
}
.invalid-feedback {
  font-size: 11px !important;
  font-family: Poppins !important;
  font-style: normal !important;
  font-weight: 500 !important;
  color: #f7553c !important;
  line-height: 130% !important;
  margin-top: 10px !important;
}

.was-validated .input-wizard-question:invalid,
.input-wizard-question.is-invalid {
  border: 1px solid #f7553c;
  background: #fee5e2;

  &::after {
    content: "";
  }
}

.is-invalid {
  display: block !important;
}

textarea::placeholder {
  color: #b7b7b7;
  font-size: 13px;
  font-family: Poppins;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

@media screen and (max-width: 600px) {
  .row-container {
    flex-direction: column;
    gap: 20px;

    .col-sm-6 {
      max-width: 100%;
    }
  }
}
.is-invalid-input {
  border: 1px solid #f7553c !important;
  background: #fee5e2 !important;

  &::after {
    content: "";
  }
}
.icon-class {
  top: 9px !important;
  right: 12px !important;
}
</style>
